import { useEffect, useLayoutEffect, useRef, useState } from "react";
import s from "../clinic.module.css";

const DRAG_THRESHOLD = 48;

export default function SwipeCarousel({
  items,
  getKey,
  renderSlide,
  hint = null,
  ariaLabel = "Карусель",
}) {
  const [index, setIndex] = useState(0);
  const [dragX, setDragX] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [viewportWidth, setViewportWidth] = useState(0);
  const dragRef = useRef(null);
  const viewportRef = useRef(null);
  const itemsKeyRef = useRef("");
  const suppressClickRef = useRef(false);

  const itemsKey = items.map((item, i) => getKey(item, i)).join("|");

  useLayoutEffect(() => {
    const el = viewportRef.current;
    if (!el) return undefined;
    const measure = () => setViewportWidth(el.clientWidth || 0);
    measure();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    window.addEventListener("resize", measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [items.length]);

  useEffect(() => {
    if (itemsKeyRef.current === itemsKey) return;
    itemsKeyRef.current = itemsKey;
    setIndex(0);
    setDragX(0);
    setDragging(false);
    dragRef.current = null;
    suppressClickRef.current = false;
  }, [itemsKey]);

  useEffect(() => {
    setIndex((prev) => Math.max(0, Math.min(prev, Math.max(items.length - 1, 0))));
  }, [items.length]);

  if (!items.length) return null;

  if (items.length === 1) {
    return <div className={s.swipeCarouselSingle}>{renderSlide(items[0], 0)}</div>;
  }

  const goTo = (i) => {
    setIndex(Math.max(0, Math.min(i, items.length - 1)));
    setDragX(0);
    setDragging(false);
  };

  const onPointerDown = (e) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    const viewport = viewportRef.current;
    if (!viewport) return;
    const width = Math.max(viewport.clientWidth, 1);
    setViewportWidth(width);
    dragRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      width,
    };
    suppressClickRef.current = false;
    setDragging(true);
    setDragX(0);
    try {
      viewport.setPointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  };

  const onPointerMove = (e) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    e.preventDefault();
    const dx = e.clientX - drag.startX;
    if (Math.abs(dx) > 6) suppressClickRef.current = true;
    let next = dx;
    if ((index === 0 && dx > 0) || (index === items.length - 1 && dx < 0)) {
      next = dx * 0.35;
    }
    setDragX(next);
  };

  const endPointer = (e) => {
    const drag = dragRef.current;
    const viewport = viewportRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    try {
      if (viewport?.hasPointerCapture?.(e.pointerId)) {
        viewport.releasePointerCapture(e.pointerId);
      }
    } catch {
      /* ignore */
    }

    const dx = e.clientX - drag.startX;
    const threshold = Math.min(DRAG_THRESHOLD, drag.width * 0.18);
    let next = index;
    if (suppressClickRef.current) {
      if (dx <= -threshold && index < items.length - 1) next = index + 1;
      else if (dx >= threshold && index > 0) next = index - 1;
    }

    dragRef.current = null;
    setIndex(next);
    setDragX(0);
    setDragging(false);
  };

  const onClickCapture = (e) => {
    if (suppressClickRef.current) {
      e.preventDefault();
      e.stopPropagation();
      suppressClickRef.current = false;
    }
  };

  const width = viewportWidth || dragRef.current?.width || 0;
  const offsetX = width ? -index * width + (dragging ? dragX : 0) : 0;

  return (
    <div className={s.swipeCarousel} aria-label={ariaLabel}>
      <div className={s.swipeCarouselMeta}>
        {hint ? <span className={s.swipeCarouselHint}>{hint}</span> : null}
        <div className={s.swipeCarouselDots} role="tablist" aria-label="Слайды">
          {items.map((item, i) => (
            <button
              key={getKey(item, i)}
              type="button"
              role="tab"
              aria-selected={i === index}
              aria-label={`${i + 1} из ${items.length}`}
              className={
                i === index
                  ? `${s.swipeCarouselDot} ${s.swipeCarouselDotOn}`
                  : s.swipeCarouselDot
              }
              onClick={() => goTo(i)}
            />
          ))}
        </div>
        <span className={s.swipeCarouselCounter}>
          {index + 1}/{items.length}
        </span>
      </div>
      <div
        className={s.swipeCarouselViewport}
        ref={viewportRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endPointer}
        onPointerCancel={endPointer}
        onClickCapture={onClickCapture}
      >
        <div
          className={`${s.swipeCarouselTrack} ${
            dragging ? s.swipeCarouselTrackDragging : ""
          }`}
          style={{
            transform: width ? `translate3d(${offsetX}px, 0, 0)` : undefined,
          }}
        >
          {items.map((item, i) => (
            <div
              key={getKey(item, i)}
              className={s.swipeCarouselSlide}
              aria-hidden={i !== index}
              style={width ? { flex: `0 0 ${width}px`, width, minWidth: width, maxWidth: width } : undefined}
            >
              {renderSlide(item, i)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
